import { create } from 'zustand'
import { SingleLocation } from '@/routes/projectStages/issue/types/issue.ts'
import {
  FORCE_LAST_SELECTED_LOCATION,
  LOCAL_STORAGE_LAST_SELECTED_LOCATION_KEY,
} from '@/lib/constants/constants.ts'

export type PendingMapIssueLocation = {
  location: string
  x: number
  y: number
  relX?: number
  relY?: number
}

type LocationMapStore = {
  showMap: boolean
  setShowMap: (showMap: boolean) => void
  mapUrl: string | null
  setMapUrl: (mapUrl: string) => void
  /**
   * Pin position. `x/y` are the stored absolute viewBox coordinates (y measured
   * from the bottom); `relX/relY` are the room-relative anchor (0..1 within the
   * clicked zone's bbox) and are absent when the click could not be resolved to a
   * zone geometry — see components/common/location/zoneAnchor.ts.
   */
  coords: { x: number; y: number; relX?: number; relY?: number } | null
  setCoords: (coords: { x: number; y: number; relX?: number; relY?: number }) => void
  selectedLocation: SingleLocation | null
  setSelectedLocation: (selectedLocation: SingleLocation | null) => void
  /**
   * "The user picked this plan" — the three writes that a pick has always meant,
   * in one action. They were spelled out at the dropdown's call site; with a second
   * picker (the header tab row) selecting the same thing, two hand-copied triples
   * would drift, and the one that drifts silently is `hoveredLocationCode`, which
   * leaves a stale room highlighted on a plan it does not belong to.
   */
  selectLocation: (location: SingleLocation | null) => void
  selectedDropdownLocation: SingleLocation | null
  setSelectedDropdownLocation: (selectedLocation: SingleLocation | null) => void
  hoveredLocationCode: string | null
  setHoveredLocationCode: (hoveredLocationCode: string | null) => void
  // Location + coords captured by tapping the issues-overview map in "Add"
  // mode. Consumed once by the New Issue dialog's fresh-open reset (see
  // IssueDialog.tsx), which seeds it into the form's `location` and clears it.
  /**
   * Pin captured by tapping a room on the stage map, waiting to seed the New Issue
   * form. relX/relY are OPTIONAL and omitted (never null) when the zone geometry
   * could not be resolved — the server drops an anchor only when x/y move without a
   * matching anchor, so omitting leaves that decision where it belongs.
   */
  pendingMapIssueLocation: PendingMapIssueLocation | null
  setPendingMapIssueLocation: (pending: PendingMapIssueLocation | null) => void
  resetValues: () => void
  setType: (type: 'issueMap' | 'previewMap') => void
  type: 'issueMap' | 'previewMap'
  initializeSelectedLocationFromLocalStorage: (
    findLocationByIri: (locations: SingleLocation[], iri: string) => SingleLocation | null,
    stageLocations: SingleLocation[],
  ) => void
}

export const useLocationMapStore = create<LocationMapStore>(set => ({
  showMap: false,
  setShowMap: showMap => set({ showMap }),
  mapUrl: null,
  setMapUrl: mapUrl => set({ mapUrl }),
  coords: null,
  setCoords: coords => set({ coords }),
  selectedLocation: null, // Initialized to null, will be updated by action
  setSelectedLocation: selectedLocation => set({ selectedLocation }),
  selectedDropdownLocation: null, // Initialized to null, will be updated by action
  setSelectedDropdownLocation: selectedDropdownLocation => set({ selectedDropdownLocation }),
  selectLocation: location =>
    set({
      selectedLocation: location,
      selectedDropdownLocation: location,
      hoveredLocationCode: null,
    }),
  hoveredLocationCode: null,
  setHoveredLocationCode: hoveredLocationCode => set({ hoveredLocationCode }),
  pendingMapIssueLocation: null,
  setPendingMapIssueLocation: pendingMapIssueLocation => set({ pendingMapIssueLocation }),
  resetValues: () =>
    set({
      showMap: false,
      mapUrl: null,
      coords: null,
      selectedLocation: null,
      selectedDropdownLocation: null,
      pendingMapIssueLocation: null,
      type: 'issueMap',
    }),
  setType: type => set({ type }),
  type: 'issueMap',

  initializeSelectedLocationFromLocalStorage: (findLocationByIri, stageLocations) => {
    const forceSelect = localStorage.getItem(FORCE_LAST_SELECTED_LOCATION)
    if (forceSelect === 'true') {
      const lastSelectedLocationIri = localStorage.getItem(LOCAL_STORAGE_LAST_SELECTED_LOCATION_KEY)
      if (lastSelectedLocationIri) {
        setTimeout(() => {
          const location = findLocationByIri(stageLocations, lastSelectedLocationIri)
          if (location) {
            set({
              selectedLocation: location,
              selectedDropdownLocation: location,
              hoveredLocationCode: null,
            })
          }
        }, 0)
      }
    }
  },
}))

export default useLocationMapStore
